import React, {
    useEffect, useState, useCallback, useContext, useMemo
} from 'react';
import PropTypes from 'prop-types';
import { SearchService } from 'gw-capability-gateway';
import { useAuthentication } from 'gw-digital-auth-react';
import { readViewModelValue } from 'gw-jutro-adapters-react';
import { TranslatorContext } from '@jutro/locale';
import { withRouter } from 'react-router-dom';

import { ViewModelForm } from 'gw-portals-viewmodel-react';
import metadata from './SearchResults.metadata.json5';

import styles from './SearchResults.module.scss';
import messages from './SearchResults.messages';

function SearchResults(props) {
    const {
        match: {
            params: { searchText }
        },
        param,
        title,
        noResultsMessage
    } = props;
    const translator = useContext(TranslatorContext);
    const [searchResult, setSearchResult] = useState({
        accounts: [],
        policies: [],
        jobs: {
            submissionJobs: [],
            policyChangeJobs: []
        }
    });
    const [loading, setLoading] = useState(true);

    const { authHeader } = useAuthentication();

    const populateAccountResults = useCallback((data) => {
        const accounts = [];
        if (data.accounts.length) {
            data.accounts.forEach((account) => {
                if (account !== null) {
                    const accountInfo = {};
                    accountInfo.accountNumber = account.accountNumber;
                    accountInfo.accountName = account.accountHolder;
                    const {
                        addressLine1, city, state, postalCode
                    } = account.accountHolderAddress;
                    const addressLine2Data = account.accountHolderAddress.addressLine2
                        ? account.accountHolderAddress.addressLine2
                        : '';

                    accountInfo.addressLine = `${addressLine1}, ${addressLine2Data}`;
                    accountInfo.addressOthers = `${city}, ${state}${postalCode}`;
                    accounts.push(accountInfo);
                }
            });
        }
        return accounts;
    }, []);

    const populatePolicyResults = useCallback((data) => {
        const policies = [];
        if (data.policies.length) {
            data.policies.forEach((policy) => {
                if (policy.policyNumber) {
                    const policyInfo = {};

                    policyInfo.policyNumber = policy.policyNumber;
                    policyInfo.accountNumber = policy.accountNumber;
                    if (policy.accountHolder.subtype === 'Company') {
                        policyInfo.accountName = policy.accountHolder.contactName;
                    } else if (policy.accountHolder.subtype === 'Person') {
                        policyInfo.accountName = `${policy.accountHolder.firstName} ${policy.accountHolder.lastName}`;
                    }
                    const {
                        addressLine1,
                        city,
                        state,
                        postalCode
                    } = policy.accountHolder.primaryAddress;
                    const addressLine2Data = policy.accountHolder.primaryAddress.addressLine2
                        ? policy.accountHolder.primaryAddress.addressLine2
                        : '';
                    policyInfo.addressLine = `${addressLine1}, ${addressLine2Data}`;
                    policyInfo.addressOthers = `${city}, ${state}${postalCode}`;
                    policies.push(policyInfo);
                }
            });
        }
        return policies;
    }, []);

    const populateJobResults = useCallback((data) => {
        const transactions = {
            submissionJobs:[],
            policyChangeJobs:[]
        };
        if (data.jobs.length) {
            data.jobs.forEach((job) => {
                if (job.jobNumber) {
                    const jobInfo = {};
                    const type = job.jobType;

                    jobInfo.policyNumber = job.jobNumber;
                    jobInfo.accountNumber = job.policy.account.accountNumber;
                    if (job.policy.account.accountHolder.subtype === 'Company') {
                        jobInfo.accountName = job.policy.account.accountHolder.contactName;
                    } else if (job.policy.account.accountHolder.subtype === 'Person') {
                        jobInfo.accountName = `${job.policy.account.accountHolder.firstName} ${job.policy.account.accountHolder.lastName}`;
                    }
                    const {
                        addressLine1,
                        city,
                        state,
                        postalCode
                    } = job.policy.account.accountHolder.primaryAddress;
                    const addressLine2Data = job.policy.account.accountHolder.primaryAddress.addressLine2
                        ? job.policy.account.accountHolder.primaryAddress.addressLine2
                        : '';
                        jobInfo.addressLine = `${addressLine1}, ${addressLine2Data}`;
                        jobInfo.addressOthers = `${city}, ${state}${postalCode}`;
                    
                    if(type == 'PolicyChange') transactions.policyChangeJobs.push(jobInfo);
                    else transactions.submissionJobs.push(jobInfo);
                }
            });
        }
        return transactions;
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            const searchObj = {
                previousSearchParam: '',
                searchParam: param || searchText
            };
            const data = await SearchService.search(searchObj, authHeader);
            setLoading(false);
            setSearchResult(() => {
                return {
                    accounts: populateAccountResults(data),
                    policies: populatePolicyResults(data),
                    jobs: populateJobResults(data)
                };
            });
        };
        fetchData();
    }, [searchText, populateAccountResults, populatePolicyResults, authHeader, param]);

    const accountDetailsOverrides = useMemo(() => {
        const overrides = searchResult.accounts.map((account, index) => {
            return {
                [`accountAccountNameLink${index}`]: {
                    to: `/accounts/${account.accountNumber}/summary`
                },
                [`accountNumber${index}`]: {
                    content: `# ${account.accountNumber}`
                },
                [`accountName${index}`]: {
                    content: `${account.accountName}`
                },
                [`accountNumberLink${index}`]: {
                    to: `/accounts/${account.accountNumber}/summary`
                },
                [`accountAddressLine${index}`]: {
                    content: `${account.addressLine}`
                },
                [`accountAddressOthers${index}`]: {
                    content: `${account.addressOthers}`
                }
            };
        });

        return Object.assign({}, ...overrides);
    }, [searchResult.accounts]);

    const policyDetailsOverrides = useMemo(() => {
        const overrides = searchResult.policies.map((policy, index) => {
            return {
                [`policyNumberLink${index}`]: {
                    to: `/policies/${policy.policyNumber}/summary`
                },
                [`policyNumber${index}`]: {
                    content: `# ${policy.policyNumber}`
                },
                [`accountName${index}`]: {
                    content: `${policy.accountName}`
                },
                [`policyAccountNameLink${index}`]: {
                    to: `/accounts/${policy.accountNumber}/summary`
                },
                [`policyAddressLine${index}`]: {
                    content: `${policy.addressLine}`
                },
                [`policyAddressOthers${index}`]: {
                    content: `${policy.addressOthers}`
                }
            };
        });

        return Object.assign({}, ...overrides);
    }, [searchResult.policies]);

    const jobDetailsOverrides = useMemo(() => {
        const overrides = searchResult.jobs.submissionJobs.map((policy, index) => {
            return {
                [`jobNumberLink${index}`]: {
                    to: `/quotes/${policy.policyNumber}/summary`
                },
                [`jobNumber${index}`]: {
                    content: `# ${policy.policyNumber}`
                },
                [`accountName${index}`]: {
                    content: `${policy.accountName}`
                },
                [`jobAccountNameLink${index}`]: {
                    to: `/accounts/${policy.accountNumber}/summary`
                },
                [`jobAddressLine${index}`]: {
                    content: `${policy.addressLine}`
                },
                [`jobAddressOthers${index}`]: {
                    content: `${policy.addressOthers}`
                }
            };
        });return Object.assign({}, ...overrides);
    }, [searchResult]);

    const jobPolicyChangeDetailsOverrides = useMemo(() => {
        const overrides = searchResult.jobs.policyChangeJobs.map((policy, index) => {
            return {
                [`jobPolicyChangeNumberLink${index}`]: {
                    to: `/change/${policy.policyNumber}/summary`
                },
                [`jobPolicyChangeNumber${index}`]: {
                    content: `# ${policy.policyNumber}`
                },
                [`policyChangeAccountName${index}`]: {
                    content: `${policy.accountName}`
                },
                [`jobPolicyChangeAccountNameLink${index}`]: {
                    to: `/accounts/${policy.accountNumber}/summary`
                },
                [`jobPolicyChangeAddressLine${index}`]: {
                    content: `${policy.addressLine}`
                },
                [`jobPolicyChangeAddressOthers${index}`]: {
                    content: `${policy.addressOthers}`
                }
            };
        });

        return Object.assign({}, ...overrides);
    }, [searchResult]);

    const overrideProps = {
        accountResults: {
            visible: searchResult.accounts.length > 0 && !loading
        },
        searchResultsTitle: {
            content: title || translator(messages.searchResults)
        },
        policyResults: {
            visible: searchResult.policies.length > 0 && !loading
        },
        jobResults: {
            visible: searchResult.jobs.submissionJobs.length > 0 && !loading
        },
        jobPolicyChangeResults: {
            visible: searchResult.jobs.policyChangeJobs.length > 0 && !loading
        },
        searchingText: {
            visible: loading
        },
        loadingIndicator: {
            loaded: !loading
        },
        noResultsMessage: {
            visible:
                (!!searchText || param)
                && !loading
                && !searchResult.accounts.length
                && !searchResult.policies.length
                && !searchResult.jobs.submissionJobs.length
                && !searchResult.jobs.policyChangeJobs.length,
            value:
                noResultsMessage
                || translator(messages.noResultsFoundFor, {
                    query: searchText || param
                })
        },
        accountHeader: {
            content: translator(
                messages.accountResults,
                { accountResultsNumber: searchResult.accounts.length }
            )
        },
        policyHeader: {
            content: translator(
                messages.policyResults,
                { policyResultsNumber: searchResult.policies.length }
            )
        },
        jobHeader: {
            content: translator(
                messages.jobResults,
                { quoteResultsNumber: searchResult.jobs.submissionJobs.length }
            )
        },
        jobPolicyChangeHeader: {
            content: translator(
                messages.jobChangeResults,
                { jobResultsNumber: searchResult.jobs.policyChangeJobs.length }
            )
        },
        ...accountDetailsOverrides,
        ...policyDetailsOverrides,
        ...jobDetailsOverrides,
        ...jobPolicyChangeDetailsOverrides
    };

    const resolvers = {
        resolveClassNameMap: styles,
    };

    const readValue = useCallback(
        (id, path) => {
            return readViewModelValue(metadata.pageContent, searchResult, id, path, overrideProps);
        },
        [overrideProps, searchResult]
    );

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={searchResult}
            overrideProps={overrideProps}
            classNameMap={resolvers.resolveClassNameMap}
            resolveValue={readValue}
        />
    );
}

SearchResults.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            searchText: PropTypes.string
        })
    }).isRequired,
    param: PropTypes.string,
    title: PropTypes.string,
    noResultsMessage: PropTypes.string
};

SearchResults.defaultProps = {
    param: '',
    title: '',
    noResultsMessage: ''
};

export default withRouter(SearchResults);
